import { Button, message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSynchronizeFinderVehicleMutation } from '../../../api/mutations/synchronizeFinderVehicle';
import {
    GetFinderVehicleSyncStatusDocument,
    useGetFinderVehicleSyncStatusQuery,
} from '../../../api/queries/getFinderVehicleSyncStatus';
import { FinderVehicleSyncStatus } from '../../../api/types';
import getApolloErrors from '../../../utilities/getApolloErrors';

type SyncFinderVehicleButtonProps = {
    companyId: string;
};

const SyncFinderVehicleButton = ({ companyId }: SyncFinderVehicleButtonProps) => {
    const { t } = useTranslation(['finderVehicleList']);

    const [syncFinderVehicle] = useSynchronizeFinderVehicleMutation();

    const { data } = useGetFinderVehicleSyncStatusQuery({
        variables: {
            companyId,
        },
    });

    const syncNow = useCallback(async () => {
        message.loading({
            content: t('finderVehicleList:messages.syncSubmission'),
            key: 'primary',
            duration: 0,
        });

        try {
            const { data } = await syncFinderVehicle({
                variables: {
                    companyId,
                },
                refetchQueries: [GetFinderVehicleSyncStatusDocument],
            });

            message.destroy('primary');

            switch (data.status) {
                case FinderVehicleSyncStatus.Running:
                    message.success({
                        content: t('finderVehicleList:messages.syncRunning'),
                        key: 'primary',
                    });
                    break;

                default:
                    message.success({
                        content: t('finderVehicleList:messages.syncIdle'),
                        key: 'primary',
                    });
                    break;
            }
        } catch (error) {
            message.destroy('primary');

            const apolloErrors = getApolloErrors(error);

            if (apolloErrors !== null) {
                const { $root: rootError } = apolloErrors;

                if (rootError) {
                    message.error(rootError);
                }
            } else {
                console.error(error);
            }
        }
    }, [companyId, syncFinderVehicle, t]);

    return (
        <Button disabled={data?.status === FinderVehicleSyncStatus.Running} onClick={syncNow} type="primary">
            {t('finderVehicleList:actions.synchronize')}
        </Button>
    );
};

export default SyncFinderVehicleButton;
